/**
 * Draw or redraw one polygon on a Leaflet map.
 *
 * Two implementations behind one component, chosen by pointer type, because the
 * two interactions are genuinely different rather than one being a fallback:
 *
 * - **Mouse:** leaflet-draw's polygon handler, with explicit Finish and Cancel
 *   controls. Finish stays disabled below three vertices, since leaflet-draw's
 *   own affordance for closing a shape is clicking the first marker, which is a
 *   few pixels wide.
 * - **Touch:** `CrosshairPolygonDraw` — a fixed crosshair and an Add Point
 *   button. leaflet-draw on a touch screen fights the map for every gesture:
 *   the tap that should place a vertex is also the tap that pans.
 *
 * Extracted from `EspNowLiveMap` so the network map draws a relay's zone with
 * exactly the same control. A second copy is how the two drift into disagreeing
 * about what a finished polygon is.
 */

import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import type { GeoJSONPolygon } from '../../types/orchard';
import CrosshairPolygonDraw from './CrosshairPolygonDraw';
import { geoJsonRingToLatLng, leafletLayerToGeoJson } from '../../utils/geoLeaflet';
import { usePointerCoarse } from './usePointerCoarse';

export interface ZoneDrawControlProps {
  color: string;
  /** Shown while redrawing, so the new outline can be placed against the old. */
  existingGeometry: GeoJSONPolygon | null;
  onZoneDrawn: (geometry: GeoJSONPolygon) => void;
  onCancel: () => void;
}

export default function ZoneDrawControl({
  color,
  existingGeometry,
  onZoneDrawn,
  onCancel,
}: ZoneDrawControlProps) {
  const map = useMap();
  const drawnRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const pointerCoarse = usePointerCoarse();

  // The callbacks are read through refs so the handler can be set up once. Re-
  // running this effect would tear down a half-drawn polygon on any parent
  // render, which on a map that polls is not a rare event.
  const onZoneDrawnRef = useRef(onZoneDrawn);
  onZoneDrawnRef.current = onZoneDrawn;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (pointerCoarse) return;
    const drawnItems = drawnRef.current;
    map.addLayer(drawnItems);

    // If there's an existing polygon, show it editably
    if (existingGeometry) {
      const ring = existingGeometry.coordinates[0];
      const latlngs = geoJsonRingToLatLng(ring);
      const poly = L.polygon(latlngs, { color, fillColor: color, fillOpacity: 0.3 });
      drawnItems.addLayer(poly);
    }

    let vertexCount = 0;
    let finishBtn: HTMLElement | null = null;

    const updateFinishBtn = () => {
      if (finishBtn) {
        finishBtn.style.opacity = vertexCount >= 3 ? '1' : '0.5';
        (finishBtn.style as any).pointerEvents = vertexCount >= 3 ? 'auto' : 'none';
      }
    };

    const handler: any = new (L as any).Draw.Polygon(map, {
      allowIntersection: true,
      showArea: true,
      touchTolerance: 0,
      shapeOptions: { color, weight: 2, fillOpacity: 0.2 },
    });

    const FinishControl = L.Control.extend({
      onAdd() {
        const c = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const btn = L.DomUtil.create('a', '', c) as HTMLAnchorElement;
        btn.href = '#';
        btn.innerHTML = '&#10003; Finish zone';
        btn.title = 'Complete polygon (need 3+ points)';
        btn.style.cssText = 'display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;padding:0 10px;height:34px;background:#16a34a;color:white;text-decoration:none;cursor:pointer;white-space:nowrap;opacity:0.5;pointer-events:none;';
        finishBtn = btn;
        L.DomEvent.on(btn, 'click', e => {
          L.DomEvent.preventDefault(e);
          // A finished (disabled) handler has no `_markers`; _finishShape would throw.
          if (vertexCount >= 3 && handler.enabled()) handler._finishShape();
        });
        return c;
      },
    });

    const CancelControl = L.Control.extend({
      onAdd() {
        const c = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const btn = L.DomUtil.create('a', '', c) as HTMLAnchorElement;
        btn.href = '#';
        btn.innerHTML = '✕ Cancel';
        btn.style.cssText = 'display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;padding:0 10px;height:34px;background:#6b7280;color:white;text-decoration:none;cursor:pointer;white-space:nowrap;';
        L.DomEvent.on(btn, 'click', e => { L.DomEvent.preventDefault(e); handler.disable(); onCancelRef.current(); });
        return c;
      },
    });

    const finishCtrl = new (FinishControl as any)({ position: 'topright' });
    const cancelCtrl = new (CancelControl as any)({ position: 'topright' });
    finishCtrl.addTo(map);
    cancelCtrl.addTo(map);

    // Track vertex count
    map.on('draw:drawvertex', () => { vertexCount++; updateFinishBtn(); });

    // Handle polygon drawn
    map.on('draw:created', (e: any) => {
      const geo = leafletLayerToGeoJson(e.layer);
      if (geo) onZoneDrawnRef.current(geo as GeoJSONPolygon);
    });

    handler.enable();

    return () => {
      handler.disable();
      map.removeLayer(drawnItems);
      try { finishCtrl.remove(); cancelCtrl.remove(); } catch { /* ignore */ }
      map.off('draw:drawvertex');
      map.off('draw:created');
    };
  }, [map, pointerCoarse]); // eslint-disable-line react-hooks/exhaustive-deps

  if (pointerCoarse) {
    const initialVertices = existingGeometry
      ? geoJsonRingToLatLng(existingGeometry.coordinates[0]).slice(0, -1)
      : undefined;
    return (
      <CrosshairPolygonDraw
        color={color}
        initialVertices={initialVertices}
        onComplete={(polygon) => onZoneDrawn(polygon as GeoJSONPolygon)}
        onCancel={onCancel}
      />
    );
  }

  return null;
}
